import { useAuth } from "AuthProvider";
import { Formik, Form as FormikForm } from "formik";
import useFetch from "hooks/useFetch";
import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  FormikTextarea,
  FormikTextField,
  Icon,
  IconButton,
  PageHeader,
  Skeleton,
  Toggle,
  styles as ui,
  useConfirm,
  useToast,
} from "stories/Admin";
import * as Yup from "yup";
import DateField from "./DateField";
import styles from "./EventPage.module.scss";
import ImageDrop from "./ImageDrop";

interface EventForm {
  tittel: string;
  tekst: string;
  start: string;
  end: string;
  plakat?: File | null;
  recurring: boolean;
  activated: boolean;
}

interface Event {
  id: number;
  tittel: string;
  tekst: string;
  start: string;
  end: string;
  plakat?: string;
  recurring: boolean;
  activated: boolean;
}

const validationSchema = Yup.object().shape({
  tittel: Yup.string().required("Overskrift er påkrevd"),
  tekst: Yup.string().required("Beskrivelse er påkrevd"),
  start: Yup.string()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, "Ugyldig tid")
    .required("Starttid er påkrevd"),
  end: Yup.string()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, "Ugyldig tid")
    .required("Sluttid er påkrevd"),
  plakat: Yup.mixed(),
});

const EventPage: React.FC = () => {
  const { selectedOrgNumber } = useAuth();
  const { isLoading, error, execute } = useFetch<any>();
  const {
    data: eventsData,
    isLoading: isEventsLoading,
    execute: fetchEvents,
  } = useFetch<Event[]>();
  const toast = useToast();
  const confirm = useConfirm();

  const [events, setEvents] = useState<Event[]>([]);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { execute: updateEvent } = useFetch<any>();

  const fetchData = async () => {
    const response = await fetchEvents(
      `/api/v1/event/org/${selectedOrgNumber}`,
      "GET",
    );
    if (response) {
      setEvents(response);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedOrgNumber]);

  useEffect(() => {
    if (!showModal && !showEditModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (showModal) handleCloseModal();
      else handleCloseEditModal();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showModal, showEditModal]);

  const handleFileChange = (file: File | null) => {
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleEditFileChange = (file: File | null) => {
    if (file) {
      setEditImagePreview(URL.createObjectURL(file));
    }
  };

  const handleTimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: Function,
    field: string,
  ) => {
    let value = e.target.value.replace(/[^0-9]/g, "");

    if (value.length > 4) {
      value = value.slice(0, 4);
    }

    if (value.length > 2) {
      value = value.slice(0, 2) + ":" + value.slice(2);
    }

    setFieldValue(field, value);
  };

  const combineDateAndTime = (
    date: Date | null,
    time: string,
  ): string | null => {
    if (!date || !time) return null;
    const [hours, minutes] = time.split(":");
    const dateWithTime = new Date(date);
    dateWithTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

    const year = dateWithTime.getFullYear();
    const month = String(dateWithTime.getMonth() + 1).padStart(2, "0");
    const day = String(dateWithTime.getDate()).padStart(2, "0");
    const hour = String(dateWithTime.getHours()).padStart(2, "0");
    const minute = String(dateWithTime.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day} ${hour}:${minute}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  /** «19:00 – 20:30 · 20.10.2026», with both dates when they differ. */
  const formatPeriod = (start: string, end: string) => {
    const startDate = formatDate(start);
    const endDate = formatDate(end);
    return startDate === endDate
      ? `${formatTime(start)} – ${formatTime(end)} · ${startDate}`
      : `${formatTime(start)} ${startDate} – ${formatTime(end)} ${endDate}`;
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  const handleImageClick = (plakat: string) => {
    setSelectedImage(plakat);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditImagePreview(null);
  };

  const handleSubmit = async (values: EventForm, { resetForm }: any) => {
    const formattedStart = combineDateAndTime(selectedStartDate, values.start);
    const formattedEnd = combineDateAndTime(selectedEndDate, values.end);

    const eventRequestDto = {
      start: formattedStart || "",
      end: formattedEnd || "",
      tittel: values.tittel,
      tekst: values.tekst,
      mosqueOrgNr: selectedOrgNumber,
      recurring: values.recurring,
      activated: values.activated,
    };

    const formData = new FormData();

    if (values.plakat) {
      formData.append("file", values.plakat);
    }

    formData.append("eventRequestDto", JSON.stringify(eventRequestDto));

    try {
      const response = await execute(
        "/api/v1/event/addWithImage",
        "POST",
        formData,
      );

      resetForm();
      setImagePreview(null);
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      setSelectedImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setEvents((prevEvents) => [...prevEvents, response]);

      fetchData();
      if (response) {
        toast.success("Aktiviteten er opprettet.");
      } else {
        toast.error("Kunne ikke opprette aktiviteten. Prøv igjen.");
      }
    } catch (error: any) {
      console.error("Error:", error.response || error.message || error);
    }
  };

  const deleteEvent = (id: number) => {
    execute(`/api/v1/event/${id}`, "DELETE")
      .then(() => {
        const updatedEvents = events.filter((event) => event.id !== id);
        setEvents(updatedEvents);
        toast.success("Aktiviteten er slettet.");
      })
      .catch((error) => {
        console.error("Failed to delete event:", error);
      });
  };

  const confirmDeleteEvent = async (event: Event) => {
    const ok = await confirm({
      title: "Slett aktivitet?",
      message: (
        <span>
          Aktiviteten <b>«{event.tittel}»</b> fjernes fra appen. Dette kan ikke
          angres.
        </span>
      ),
      confirmLabel: "Slett aktivitet",
      tone: "danger",
    });
    if (ok) deleteEvent(event.id);
  };

  const openEditModal = (event: Event) => {
    setEditingEvent(event);
    setShowEditModal(true);
    setSelectedStartDate(new Date(event.start));
    setSelectedEndDate(new Date(event.end));
    setEditImagePreview(event.plakat || null);
  };

  const handleSaveEdit = async (values: EventForm) => {
    if (!editingEvent) return;

    const formattedStart = combineDateAndTime(selectedStartDate, values.start);
    const formattedEnd = combineDateAndTime(selectedEndDate, values.end);

    const eventRequestDto = {
      tittel: values.tittel,
      tekst: values.tekst,
      start: formattedStart || "",
      end: formattedEnd || "",
      mosqueOrgNr: selectedOrgNumber,
      recurring: values.recurring,
      activated: values.activated,
    };

    const formData = new FormData();
    formData.append("eventRequestDto", JSON.stringify(eventRequestDto));

    if (values.plakat) {
      formData.append("file", values.plakat);
    }

    try {
      await updateEvent(
        `/api/v1/event/${selectedOrgNumber}/${editingEvent.id}`,
        "PATCH",
        formData,
      );

      setEvents((prev) =>
        prev.map((event) =>
          event.id === editingEvent.id
            ? {
                ...event,
                tittel: values.tittel,
                tekst: values.tekst,
                activated: values.activated,
                start: formattedStart || event.start,
                end: formattedEnd || event.end,
                plakat: values.plakat
                  ? URL.createObjectURL(values.plakat)
                  : event.plakat,
              }
            : event,
        ),
      );

      setShowEditModal(false);
      setEditingEvent(null);
      fetchData();
      toast.success("Endringene er lagret.");
    } catch (error) {
      console.error("Failed to update event", error);
    }
  };

  /** Fields shared by the create form and the edit dialog. */
  const renderFields = (
    values: EventForm,
    setFieldValue: (field: string, value: any) => void,
    image: React.ReactNode,
  ) => (
    <>
      <FormikTextField
        name="tittel"
        label="Overskrift"
        required
        placeholder="Skriv overskrift …"
      />
      <FormikTextarea
        name="tekst"
        label="Beskrivelse"
        required
        rows={3}
        placeholder="Skriv beskrivelse …"
      />
      <div className={ui.g2}>
        <FormikTextField
          name="start"
          label="Starttid"
          required
          placeholder="TT:MM"
          inputMode="numeric"
          className={ui.num}
          value={values.start}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleTimeChange(e, setFieldValue, "start")
          }
        />
        <DateField
          label="Startdato"
          required
          selected={selectedStartDate}
          onChange={(date) => setSelectedStartDate(date)}
        />
        <FormikTextField
          name="end"
          label="Sluttid"
          required
          placeholder="TT:MM"
          inputMode="numeric"
          className={ui.num}
          value={values.end}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleTimeChange(e, setFieldValue, "end")
          }
        />
        <DateField
          label="Sluttdato"
          required
          selected={selectedEndDate}
          onChange={(date) => setSelectedEndDate(date)}
        />
      </div>
      {image}
      <Toggle
        label={<span className={styles.toggleLabel}>Vis i appen</span>}
        name="activated"
        checked={values.activated}
        onChange={(e) => setFieldValue("activated", e.target.checked)}
      />
      <Toggle
        label={
          <span className={styles.toggleText}>
            <span className={styles.toggleLabel}>Gjentagende aktivitet</span>
            <span className={styles.help}>Vises som fast aktivitet</span>
          </span>
        }
        name="recurring"
        checked={values.recurring}
        onChange={(e) => setFieldValue("recurring", e.target.checked)}
      />
    </>
  );

  return (
    <>
      <PageHeader
        title="Aktiviteter"
        subtitle="Arrangementer og kurs som vises i appen."
      />

      <div className={styles.layout}>
        <Card title="Ny aktivitet">
          <Formik
            initialValues={{
              tittel: editingEvent?.tittel || "",
              tekst: editingEvent?.tekst || "",
              start: editingEvent ? formatTime(editingEvent.start) : "",
              end: editingEvent ? formatTime(editingEvent.end) : "",
              recurring: editingEvent?.recurring || false,
              activated: editingEvent?.activated || true,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isValid, dirty, setFieldValue, values }) => (
              <FormikForm className={styles.form} noValidate>
                {renderFields(
                  values,
                  setFieldValue,
                  <ImageDrop
                    label="Overskriftsbilde"
                    required
                    preview={imagePreview}
                    inputRef={fileInputRef}
                    onFile={(file) => {
                      setFieldValue("plakat", file);
                      handleFileChange(file);
                    }}
                  />,
                )}
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={!(isValid && dirty)}
                  icon={<Icon name="check" size={16} strokeWidth={2} />}
                >
                  {isLoading ? "Laster opp …" : "Opprett aktivitet"}
                </Button>
                {error && (
                  <Alert tone="error">Noe gikk galt. Prøv igjen senere.</Alert>
                )}
              </FormikForm>
            )}
          </Formik>
        </Card>

        <Card title="Alle aktiviteter">
          {isEventsLoading ? (
            <div className={styles.skeletons}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={72} radius={12} />
              ))}
            </div>
          ) : error ? (
            <Alert tone="error">Kunne ikke hente aktivitetene.</Alert>
          ) : events.length > 0 ? (
            <ul className={styles.list}>
              {events.map((event) => (
                <li
                  key={event.id}
                  className={`${styles.item} ${
                    !event.activated ? styles.itemInactive : ""
                  }`}
                >
                  {event.plakat && event.plakat.trim() !== "" ? (
                    <button
                      type="button"
                      className={styles.thumb}
                      onClick={() => handleImageClick(event.plakat || "")}
                      aria-label={`Vis bildet for ${event.tittel}`}
                    >
                      <img src={event.plakat} alt="" />
                    </button>
                  ) : (
                    <span className={styles.thumb} aria-hidden="true">
                      <Icon name="cal" size={20} />
                    </span>
                  )}
                  <span className={styles.itemBody}>
                    <span className={styles.itemTitleRow}>
                      <b className={styles.itemTitle}>{event.tittel}</b>
                      {event.recurring && (
                        <Badge tone="lilac">Gjentagende</Badge>
                      )}
                      {!event.activated && <Badge tone="grey">Skjult</Badge>}
                    </span>
                    <span className={styles.itemText}>
                      {truncateText(event.tekst, 100)}
                    </span>
                    <span className={styles.itemWhen}>
                      <Icon name="cal" size={14} strokeWidth={2} />
                      {formatPeriod(event.start, event.end)}
                    </span>
                  </span>
                  <IconButton
                    label="Rediger"
                    icon={<Icon name="edit" size={17} strokeWidth={1.9} />}
                    onClick={() => openEditModal(event)}
                  />
                  <IconButton
                    label="Slett"
                    icon={<Icon name="trash" size={17} strokeWidth={1.9} />}
                    onClick={() => confirmDeleteEvent(event)}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon="cal" title="Ingen aktiviteter">
              Aktiviteter du oppretter vises her.
            </EmptyState>
          )}
        </Card>
      </div>

      {showEditModal && editingEvent && (
        <div
          className={ui.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleCloseEditModal();
          }}
        >
          <div
            className={ui.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-event-title"
          >
            <Formik
              initialValues={{
                tittel: editingEvent.tittel,
                tekst: editingEvent.tekst,
                start: editingEvent ? formatTime(editingEvent.start) : "",
                end: editingEvent ? formatTime(editingEvent.end) : "",
                recurring: editingEvent.recurring,
                activated: editingEvent.activated,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSaveEdit}
            >
              {({ isValid, values, setFieldValue, isSubmitting }) => (
                <FormikForm noValidate>
                  <h2 className={ui.mh} id="edit-event-title">
                    <span className={ui.mhIcon} aria-hidden="true">
                      <Icon name="edit" size={18} />
                    </span>
                    Rediger aktivitet
                    <IconButton
                      className={styles.modalClose}
                      label="Lukk"
                      icon={<Icon name="x" size={18} strokeWidth={2} />}
                      onClick={handleCloseEditModal}
                    />
                  </h2>
                  <div className={ui.mb}>
                    {renderFields(
                      values,
                      setFieldValue,
                      <ImageDrop
                        label="Overskriftsbilde"
                        preview={editImagePreview}
                        onFile={(file) => {
                          setFieldValue("plakat", file);
                          handleEditFileChange(file);
                        }}
                      />,
                    )}
                  </div>
                  <div className={ui.mf}>
                    <Button variant="secondary" onClick={handleCloseEditModal}>
                      Avbryt
                    </Button>
                    <Button
                      type="submit"
                      disabled={!isValid}
                      loading={isSubmitting}
                    >
                      Lagre endringer
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>
        </div>
      )}

      {showModal && selectedImage && (
        <div
          className={ui.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleCloseModal();
          }}
        >
          <div
            className={`${ui.modal} ${styles.imageModal}`}
            role="dialog"
            aria-modal="true"
            aria-label="Bilde"
          >
            <div className={ui.mh}>
              Bilde
              <IconButton
                className={styles.modalClose}
                label="Lukk"
                icon={<Icon name="x" size={18} strokeWidth={2} />}
                onClick={handleCloseModal}
              />
            </div>
            <div className={ui.mb}>
              <img
                src={selectedImage}
                alt="Bilde i full størrelse"
                className={styles.fullImage}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventPage;
